import {
  getStatementEndToEndId,
  getStatementOperationId,
} from "./mapStatementTransactionRow";

/**
 * Builds a ReceiptModal payload from an IB/BaaS statement row when the API
 * does not embed a nested `receipt` object.
 */
export const buildReceiptFromStatementItem = (item) => {
  if (!item) return null;

  const existing = item.receipt;
  if (existing && typeof existing === "object" && Object.keys(existing).length > 0) {
    return existing;
  }

  const legs = Array.isArray(item.batch_legs) ? item.batch_legs : [];
  const isBatch = legs.length > 1 || Number(item.leg_count ?? item.legCount ?? 0) > 1;
  const amountCents = Math.abs(
    Number(
      item.batch_total_amount
        ?? item.batchTotalAmount
        ?? item.amount
        ?? item.operation_amount
        ?? 0,
    ),
  );
  const isRefund = Boolean(item.is_refund ?? item.isRefund);
  const endToEnd = getStatementEndToEndId(item);
  const operationId =
    getStatementOperationId(item) || endToEnd || String(item.id || "");
  const batchId = String(item.batch_id ?? item.batchId ?? "").trim();

  const detailItems = [
    { title: "Descrição", value: item.description || "" },
    { title: "Categoria", value: item.category?.name || "" },
    { title: "Tipo", value: item.type || "" },
    {
      title: "ID da operação",
      value: operationId && operationId !== endToEnd ? operationId : "",
    },
    { title: "End-to-end", value: endToEnd },
    { title: "Lote", value: batchId },
    {
      title: "Status do lote",
      value: item.batch_status || item.batchStatus || "",
    },
  ].filter((row) => Boolean(row.value));

  const details = [
    {
      value: isRefund ? "Detalhes do estorno" : "Detalhes da transação",
      items: detailItems,
    },
  ];

  if (isBatch && legs.length > 0) {
    details.push({
      value: "Pernas do lote",
      items: legs.map((leg, index) => {
        const idx = leg.leg_index ?? leg.legIndex ?? index + 1;
        const count = leg.leg_count ?? leg.legCount ?? legs.length;
        const legAmount = Math.abs(Number(leg.amount ?? leg.operation_amount ?? 0));
        const fee = Math.abs(Number(leg.fee_amount ?? leg.feeAmount ?? 0));
        const e2e = getStatementEndToEndId(leg);
        const status = String(leg.status || leg.batch_status || "").trim();
        const parts = [
          `${idx}/${count}`,
          `R$ ${(legAmount / 100).toFixed(2).replace(".", ",")}`,
        ];
        if (fee > 0) parts.push(`tarifa R$ ${(fee / 100).toFixed(2).replace(".", ",")}`);
        if (status) parts.push(status);
        if (e2e) parts.push(e2e);
        return {
          title: `Pix ${idx}/${count}`,
          value: parts.join(" · "),
        };
      }),
    });
  }

  return {
    amount: amountCents,
    date: item.created_at || new Date().toISOString(),
    type: isRefund ? "refound" : "",
    transactionId: String(batchId || operationId),
    supportPhone: "0800 000 0000",
    details,
  };
};

/** AccountBalanceCategoryId = 6 (ESTORNO / devolução PIX). */
const CATEGORY_PIX_RETURN = "6";

const normalizeRefundText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * True for IB/BaaS statement rows that belong on /estornos:
 * flagged is_refund, category ESTORNO, or MD06 / titularidade / devolução text.
 */
export const isStatementRefund = (item) => {
  if (!item) return false;
  if (item.is_refund === true || item.isRefund === true) return true;
  const receiptType = String(item?.receipt?.type ?? "").toLowerCase();
  if (receiptType === "refound" || receiptType === "refund") return true;
  const categoryId = String(item?.category?.id ?? item?.category_id ?? "");
  if (categoryId === CATEGORY_PIX_RETURN) return true;
  const haystack = normalizeRefundText(
    [item?.description, item?.name, item?.category?.name].filter(Boolean).join(" "),
  );
  return /titularidade|divergente|devolucao|estorno|chargeback/.test(haystack);
};
